import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { useGetMeQuery } from 'store/services/user/meApi';
import { meApi } from 'store/services/user/meApi';
import {
  useGetSubscriptionStatusQuery,
  useGetBillingQuery,
  useCreateCheckoutSessionMutation,
  useCreatePortalSessionMutation,
} from 'store/services/subscription/subscriptionApi';
import { AssetUrls } from 'config/assets';
import Points from 'components/Points';
import { MONTHLY_MAINTENANCE_FEE_POINTS } from 'config/fees';
import {
  FREE_TIER_MONTHLY_ALERT_LIMIT_US,
  FREE_TIER_MONTHLY_ALERT_LIMIT_INTL,
  ELITE_TIER_MONTHLY_ALERT_LIMIT_US,
  ELITE_TIER_MONTHLY_ALERT_LIMIT_INTL,
} from 'constants/haveListAlerts';
import { useTransactionFeeRate } from 'hooks/useTransactionFeeRate';
import { eliteFeeCopy, formatFeePct } from 'config/economyCopy';
import { toast } from 'react-toastify';
import FeedbackModal from 'components/FeedbackModal/FeedbackModal';
import { useSubmitFeedbackMutation } from 'store/services/feedback/feedbackApi';
import { useConfetti } from 'hooks/useConfetti';
import { getTmRefCookie } from 'utils/referralCookies';
import BorderColorPickerModal from 'components/BorderColorPickerModal/BorderColorPickerModal';
import BubbleColorPickerModal from 'components/BubbleColorPickerModal/BubbleColorPickerModal';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import {
  AnimatedBorderCard,
  EliteBadgeCard,
  TrophyCaseCard,
  ChatCustomizationCard,
  CustomUrlCard,
  SocialLinksCard,
  SmsAlertsCard,
  ZeroFeesCard,
} from './FeatureCards';
import SavingsBanner from './components/SavingsBanner/SavingsBanner';
import AnnualUpsellCard from './components/AnnualUpsellCard/AnnualUpsellCard';
import './elite.css';

const BNG_47_SCRYFALL_ID = '546dae51-070c-4f3f-b0b4-6b88ff314af1';

type FeatureRow = {
  label: string;
  free: React.ReactNode;
  elite: React.ReactNode;
  anchor?: string;
};

const SHARED_FEATURES: FeatureRow[] = [
  { label: 'Unlimited trading', free: '✓', elite: '✓' },
  { label: 'Have and want list management', free: '✓', elite: '✓' },
  { label: 'Bulk CSV import', free: '✓', elite: '✓' },
  { label: 'Auto-match engine', free: '✓', elite: '✓' },
];

const ElitePage: React.FC = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [searchParams] = useSearchParams();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showBorderColorModal, setShowBorderColorModal] = useState(false);
  const [showBubbleColorModal, setShowBubbleColorModal] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationVisible, setCelebrationVisible] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('annual');
  const celebrationFired = useRef(false);

  const { triggerEliteCelebration } = useConfetti();

  const isSuccess = searchParams.get('success') === 'true';
  const isCanceled = searchParams.get('canceled') === 'true';
  const urlSource = searchParams.get('source');
  const urlRef = searchParams.get('ref') ?? undefined;
  const variantParam = (searchParams.get('variant') || 'A').toUpperCase();
  const eliteVariantClass = variantParam === 'B'
    ? 'elite-compare-card--variant-b'
    : 'elite-compare-card--variant-a';

  const { data: meData } = useGetMeQuery(undefined, {
    skip: !currentUser,
  });

  const isElite = meData?.isElite || false;

  // SMS-alert caps shown on this page personalize to the viewer's profile country.
  // (The cap the backend actually enforces is keyed off the phone number — see
  // getMonthlyAlertLimit. Unknown / logged-out country falls back to the US caps.)
  const country = meData?.country ?? null;
  const isInternationalRegion = country !== null && country !== 'US' && country !== 'CA';
  const smsAlertsFreeCap = isInternationalRegion
    ? FREE_TIER_MONTHLY_ALERT_LIMIT_INTL
    : FREE_TIER_MONTHLY_ALERT_LIMIT_US;
  const smsAlertsEliteCap = isInternationalRegion
    ? ELITE_TIER_MONTHLY_ALERT_LIMIT_INTL
    : ELITE_TIER_MONTHLY_ALERT_LIMIT_US;

  // Both numbers are live: the admin-controlled rate and the Elite discount
  // off it. Neither has a default, so nothing flashes a stale "1%".
  const { ratePct, eliteDiscountPct } = useTransactionFeeRate();

  const eliteSpecificFeatures: FeatureRow[] = [
    {
      label: 'Monthly maintenance fee',
      free: <Points points={MONTHLY_MAINTENANCE_FEE_POINTS} type="small" />,
      elite: <span className="elite-compare-feature-row__waived">Waived</span>,
    },
    {
      label: 'Transaction fee',
      free: ratePct != null ? <span>{formatFeePct(ratePct)}</span> : null,
      elite:
        eliteDiscountPct == null ? null : eliteDiscountPct >= 100 ? (
          <span className="elite-compare-feature-row__waived">Waived</span>
        ) : (
          <span>{formatFeePct(eliteDiscountPct)} off</span>
        ),
    },
    { label: 'SMS trade alerts', free: `${smsAlertsFreeCap} / month`, elite: `${smsAlertsEliteCap} / month`, anchor: 'feature-sms-alerts' },
    { label: 'Badge display', free: '1', elite: 'Up to 4', anchor: 'feature-elite-badge' },
    { label: 'Animated avatar border', free: '—', elite: '✓', anchor: 'feature-animated-border' },
    { label: 'Custom border colors', free: '—', elite: '✓', anchor: 'feature-animated-border' },
    { label: 'Chat bubble customization', free: '—', elite: '✓', anchor: 'feature-chat-customization' },
    { label: 'Custom profile slug', free: '—', elite: '✓', anchor: 'feature-custom-url' },
    { label: 'Custom emoji reactions', free: '—', elite: '✓', anchor: 'feature-chat-customization' },
    { label: 'Social links on profile', free: '—', elite: '✓', anchor: 'feature-social-links' },
    { label: 'Prefer domestic trades', free: '—', elite: '✓' },
    { label: 'Zero ads', free: '—', elite: '✓' },
  ];

  const features: FeatureRow[] = [...SHARED_FEATURES, ...eliteSpecificFeatures];
  const freeFeatures = features.filter((f) => f.free !== '—');

  const { data: subscriptionStatus } = useGetSubscriptionStatusQuery(undefined, {
    skip: !currentUser || !isElite,
  });

  const { data: billing } = useGetBillingQuery(undefined, {
    skip: !currentUser || !isElite,
  });

  const [createCheckout, { isLoading: isCheckoutLoading }] = useCreateCheckoutSessionMutation();
  const [createPortalSession, { isLoading: isPortalLoading }] = useCreatePortalSessionMutation();
  const [submitFeedback, { isLoading: isSubmittingFeedback }] = useSubmitFeedbackMutation();
  const navigate = useNavigate();

  // Refresh user data after successful checkout
  useEffect(() => {
    if (isSuccess) {
      dispatch(meApi.util.invalidateTags(['User']));
    }
  }, [isSuccess, dispatch]);

  // Celebration sequence on successful checkout
  useEffect(() => {
    if (isSuccess && !celebrationFired.current) {
      celebrationFired.current = true;

      // Remove ?success=true from URL immediately
      window.history.replaceState({}, '', window.location.pathname);

      // Show celebration overlay
      setShowCelebration(true);
      // Trigger fade-in on next frame
      requestAnimationFrame(() => {
        setCelebrationVisible(true);
      });

      // Fire confetti
      triggerEliteCelebration();

      // Auto-dismiss after 4 seconds
      const timer = setTimeout(() => {
        setCelebrationVisible(false);
        setTimeout(() => setShowCelebration(false), 600);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [isSuccess, triggerEliteCelebration]);

  const dismissCelebration = useCallback(() => {
    setCelebrationVisible(false);
    setTimeout(() => setShowCelebration(false), 600);
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  const handleSubscribe = async (source?: string) => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    try {
      const result = await createCheckout({
        source: urlSource || source || 'elite-page',
        billingInterval,
        // URL ref wins; otherwise fall back to the tm_ref attribution cookie so
        // a checkout started days after the referred landing still attributes.
        // The backend also reads the cookie directly — this body param is the
        // reliable path when the cookie doesn't cross to the API subdomain.
        ref: urlRef ?? getTmRefCookie() ?? undefined,
      }).unwrap();
      window.location.href = result.url;
    } catch {
      toast.error('Failed to start checkout. Please try again.');
    }
  };

  const scrollToFeature = (anchorId: string) => {
    document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleFeedbackSubmit = async (feedbackData: any) => {
    try {
      const result = await submitFeedback(feedbackData).unwrap();
      if (result.success) {
        toast.success('Thank you for your feedback!');
        setIsFeedbackModalOpen(false);
      } else {
        toast.error(result.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  const handleManageSubscription = () => {
    navigate('/settings/billing');
  };

  const handleSwitchToAnnual = async () => {
    try {
      const result = await createPortalSession({ flow: 'subscription_update_confirm' }).unwrap();
      window.location.href = result.url;
    } catch {
      toast.error('Unable to open the annual plan switch. Please try again or contact support.');
    }
  };

  const isAnnual = billing?.planInterval === 'year';
  const planAmountCents = billing?.planAmount ?? 599;
  const monthlyPriceDisplay = isAnnual
    ? `$${(planAmountCents / 12 / 100).toFixed(2)}/month`
    : `$${(planAmountCents / 100).toFixed(2)}/month`;
  const annualBilledLine = isAnnual
    ? `$${(planAmountCents / 100).toFixed(2)} billed annually`
    : null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderFeatureCards = (elite: boolean) => (
    <div className="elite-feature-cards-list">
      <div id="feature-zero-fees">
        <ZeroFeesCard isElite={elite} />
      </div>
      <div id="feature-sms-alerts">
        <SmsAlertsCard isElite={elite} eliteCap={smsAlertsEliteCap} />
      </div>
      <div id="feature-animated-border">
        <AnimatedBorderCard
          isElite={elite}
          avatarUrl={meData?.avatarUrl}
          username={meData?.username}
          borderColor1={meData?.borderColor1}
          borderColor2={meData?.borderColor2}
          onOpenBorderColorModal={() => setShowBorderColorModal(true)}
        />
      </div>
      <div id="feature-chat-customization">
        <ChatCustomizationCard
          isElite={elite}
          bubbleColor1={meData?.bubbleColor1}
          bubbleColor2={meData?.bubbleColor2}
          onOpenBubbleColorModal={() => setShowBubbleColorModal(true)}
        />
      </div>
      <div id="feature-elite-badge">
        <EliteBadgeCard isElite={elite} />
      </div>
      <div id="feature-social-links">
        <SocialLinksCard isElite={elite} username={meData?.username} />
      </div>
      <div id="feature-custom-url">
        <CustomUrlCard
          isElite={elite}
          customSlug={meData?.customSlug}
          customSlugSetAt={meData?.customSlugSetAt}
        />
      </div>
      <div id="feature-trophy-case">
        <TrophyCaseCard isElite={elite} username={meData?.username} />
      </div>
    </div>
  );

  const renderEliteManagement = () => (
    <div className="elite-management-section">
      <div className="elite-status-card">
        <div className="elite-status-header">
          <img src={AssetUrls.icons.elite.badge} alt="Elite Badge" className="elite-status-badge" />
          <div>
            <h2 className="elite-status-title">TradeMagic Elite</h2>
            <span className="elite-status-active">Active</span>
          </div>
        </div>

        <div className="elite-status-details">
          {subscriptionStatus?.eliteSince && (
            <div className="elite-status-row">
              <span className="elite-status-label">Member since</span>
              <span className="elite-status-value">{formatDate(subscriptionStatus.eliteSince)}</span>
            </div>
          )}
          {subscriptionStatus?.currentPeriodEnd && (
            <div className="elite-status-row">
              <span className="elite-status-label">Next renewal</span>
              <span className="elite-status-value">{formatDate(subscriptionStatus.currentPeriodEnd)}</span>
            </div>
          )}
          <div className="elite-status-row">
            <span className="elite-status-label">Plan</span>
            <div className="elite-status-value">
              <div>{monthlyPriceDisplay}</div>
              {annualBilledLine && (
                <div className="elite-status-value-sub">{annualBilledLine}</div>
              )}
            </div>
          </div>
          <div className="elite-status-row">
            <span className="elite-status-label">Ad-free experience</span>
            <span className="elite-status-value elite-status-value--accent">Always</span>
          </div>
        </div>

        <button
          className="elite-manage-button"
          onClick={handleManageSubscription}
        >
          Manage Subscription
        </button>
      </div>

      <SavingsBanner eliteSince={billing?.eliteSince ?? subscriptionStatus?.eliteSince} />

      <AnnualUpsellCard
        planInterval={billing?.planInterval}
        currentPeriodEnd={billing?.currentPeriodEnd ?? subscriptionStatus?.currentPeriodEnd}
        onSwitchToAnnual={handleSwitchToAnnual}
        isSubmitting={isPortalLoading}
      />

      {renderFeatureCards(true)}

      <div className="elite-thankyou-section">
        <h2 className="elite-thankyou-heading">Thank you for supporting TradeMagic.</h2>
        <p className="elite-thankyou-body">
          Your subscription helps us build the best peer-to-peer trading platform for Magic: The
          Gathering cards. Every feature on this platform exists because of traders like you.
        </p>
        <p className="elite-thankyou-body">
          Have an idea or feedback?{' '}
          <button
            className="elite-thankyou-link"
            onClick={() => setIsFeedbackModalOpen(true)}
          >
            Let us know &rarr;
          </button>
        </p>
      </div>
    </div>
  );

  const heroBgUrl = AssetUrls.cards.getCardImage(BNG_47_SCRYFALL_ID, 'art_crop');

  const renderFeatureShowcase = () => (
    <div className="elite-showcase-section">
      <header className="elite-header">
        <img src={AssetUrls.icons.elite.badge} alt="Elite Badge" className="elite-header-badge" />
        <h1 className="elite-title">TradeMagic Elite</h1>
        <p className="elite-subtitle">
          {ratePct != null && eliteDiscountPct != null && eliteDiscountPct < 100
            ? `No maintenance fee, ${eliteFeeCopy(ratePct, eliteDiscountPct)}, zero ads, and exclusive premium cosmetic features`
            : 'Zero fees, zero ads, and unlock exclusive premium cosmetic features'}
        </p>
      </header>

      <div className="elite-billing-toggle" aria-label="Billing interval">
        <span
          className={`elite-billing-toggle__label ${billingInterval === 'monthly' ? 'elite-billing-toggle__label--active' : ''}`}
        >
          Monthly
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={billingInterval === 'annual'}
          aria-label="Toggle yearly billing"
          className={`elite-billing-switch ${billingInterval === 'annual' ? 'elite-billing-switch--on' : ''}`}
          onClick={() => setBillingInterval(billingInterval === 'annual' ? 'monthly' : 'annual')}
        >
          <span className="elite-billing-switch__thumb" />
        </button>
        <span
          className={`elite-billing-toggle__label ${billingInterval === 'annual' ? 'elite-billing-toggle__label--active' : ''}`}
        >
          Yearly
        </span>
        <span
          className={`elite-billing-toggle__savings ${billingInterval === 'annual' ? 'elite-billing-toggle__savings--visible' : ''}`}
        >
          Save 33%
        </span>
      </div>

      <div className="elite-compare-grid">
        <div className="elite-compare-card elite-compare-card--free">
          <div className="elite-compare-card__header">
            <h2 className="elite-compare-card__label">Free</h2>
            <div className="elite-compare-card__price">
              <span className="elite-compare-card__price-main">$0.00</span>
              <span className="elite-compare-card__price-sub">/ forever</span>
            </div>
          </div>
          <ul className="elite-compare-features">
            {freeFeatures.map((f) => (
              <li key={`free-${f.label}`} className="elite-compare-feature-row">
                <span className="elite-compare-feature-row__label">{f.label}</span>
                <span className="elite-compare-feature-row__value">{f.free}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={`elite-compare-card elite-compare-card--elite ${eliteVariantClass}`}>
          <div className="elite-compare-card__header">
            <div className="elite-compare-card__label-row">
              <img src={AssetUrls.icons.elite.badge} alt="" className="elite-compare-card__badge" />
              <h2 className="elite-compare-card__label">Elite</h2>
            </div>
            <div className="elite-compare-card__price">
              {billingInterval === 'annual' ? (
                <>
                  <span className="elite-compare-card__price-main">$3.99</span>
                  <span className="elite-compare-card__price-sub">/month</span>
                </>
              ) : (
                <>
                  <span className="elite-compare-card__price-main">$5.99</span>
                  <span className="elite-compare-card__price-sub">/month</span>
                </>
              )}
            </div>
            {billingInterval === 'annual' && (
              <p className="elite-compare-card__price-billed">$47.88 billed annually</p>
            )}
          </div>
          <button
            className="elite-subscribe-button elite-subscribe-button--shimmer"
            onClick={() => handleSubscribe('elite-page')}
            disabled={isCheckoutLoading}
          >
            {isCheckoutLoading ? 'Loading...' : 'Subscribe Now'}
          </button>
          <p className="elite-price-note">No commitment, cancel anytime.</p>
          <p className="elite-price-note">Everything in Free, plus:</p>
          <ul className="elite-compare-features">
            {eliteSpecificFeatures.map((f) => {
              const isAnchorable = f.anchor && f.elite !== '—';
              return (
                <li key={`elite-${f.label}`} className="elite-compare-feature-row">
                  {isAnchorable ? (
                    <button
                      type="button"
                      className="elite-compare-feature-row__label elite-compare-feature-row__label--anchor"
                      onClick={() => scrollToFeature(f.anchor as string)}
                    >
                      {f.label}
                    </button>
                  ) : (
                    <span className="elite-compare-feature-row__label">{f.label}</span>
                  )}
                  <span className="elite-compare-feature-row__value">{f.elite}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {renderFeatureCards(false)}

      <div className="elite-free-section">
        <h2 className="elite-free-heading">Trading is free. Always.</h2>
        <p className="elite-free-body">
          A trading platform only works when everyone can trade. No paid matchmaking, no trade
          slots, no points-for-cash. Elite is how you support the platform and make your account
          your own.
        </p>
        <button
          className="elite-subscribe-button elite-subscribe-button--shimmer"
          onClick={() => handleSubscribe('elite-page-bottom')}
          disabled={isCheckoutLoading}
        >
          {isCheckoutLoading ? 'Loading...' : 'Subscribe Now'}
        </button>
      </div>

      {isCanceled && (
        <p className="elite-canceled-note">
          Checkout was canceled. No charges were made. Feel free to subscribe whenever you're ready.
        </p>
      )}
    </div>
  );

  return (
    <>
      <SEOHelmet
        title="TradeMagic Elite - Premium MTG Trading Subscription"
        description="Upgrade to TradeMagic Elite for exclusive premium cosmetic features, priority support, and more. $5.99/month."
        canonical="https://trademagic.gg/elite"
        keywords="TradeMagic Elite, premium MTG trading, Magic card trading, TradeMagic subscription"
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Elite', url: 'https://trademagic.gg/elite' },
        ]}
      />

      <div className="elite-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="elite-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="elite-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/elite', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div
            className="elite-main elite-main--showcase"
            style={{ '--elite-hero-bg': `url(${heroBgUrl})` } as React.CSSProperties}
          >
            <TopNavigationBar
              title="Elite"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="elite-container">
              {isElite || showCelebration
                ? renderEliteManagement()
                : renderFeatureShowcase()
              }
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>

      <BorderColorPickerModal
        isOpen={showBorderColorModal}
        onClose={() => setShowBorderColorModal(false)}
        currentColor1={meData?.borderColor1 || '#FFD700'}
        currentColor2={meData?.borderColor2 || '#9B59B6'}
        avatarUrl={meData?.avatarUrl}
        username={meData?.username}
        onSaveOverride={!isElite ? () => handleSubscribe('border-colors') : undefined}
      />

      <BubbleColorPickerModal
        isOpen={showBubbleColorModal}
        onClose={() => setShowBubbleColorModal(false)}
        currentColor1={meData?.bubbleColor1 || '#8B5CF6'}
        currentColor2={meData?.bubbleColor2 || '#6D28D9'}
        onSaveOverride={!isElite ? () => handleSubscribe('bubble-colors') : undefined}
      />

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        isLoading={isSubmittingFeedback}
      />

      {showCelebration && (
        <div
          className={`elite-celebration-overlay ${celebrationVisible ? 'elite-celebration-overlay--visible' : ''}`}
          onClick={dismissCelebration}
        >
          <div className="elite-celebration-content">
            <EliteBadge isElite size={256} />
            <h1 className="elite-celebration-title">You Are Elite</h1>
            <p className="elite-celebration-subtitle">
              Welcome to the club. Your cosmetic features are now active.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ElitePage;
